/* eslint-disable complexity */
import {
    Box,
    Button,
    Flex,
    InlineFlex,
    Select as PlasticSelect,
    Text as PlasticText,
    THEME_CONSTANTS
} from '@cof/plastic-components';
import { withText } from '../../../text/textStore';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState, useRef, useContext } from 'react';
import { css, styled } from 'styled-components';
import { DefaultText, FullInputWidth } from './SharedStyledComponents';
import ErrorHandler, { shouldDisplayFormTick } from './ErrorHandler';
import Label from '../../../components/Label';
import CheckedLabel from '../../../components/CheckedLabel';
import { autoCompleteMapping, FORM_SECTIONS } from './FormIDs';
import Group from '../../../components/Group';
import Legend from '../../../components/Legend';
import AddressLookup from './AddressLookup';
import { useAddressLookupData } from '../../../data';
import pushToDataLayer, { EVENTS } from '../../../utilities/dataLayer';
import { FormFocusContext } from '../../../components/FormFocusContext';
import { useLandmarkRefsContext } from '../../../components/LandmarkRefsProvider';
import { FormUtilsContext } from '../QuickCheck';
import { useFormData } from '../../../components/FormDataContext';
import { LabelSection } from '../../../components/TextField/TextField';
import { getAriaDescribedBy, houseFlatNumberRegex, postcodeRegex } from '../../../utilities/utils';
// TODO: Further Improvement - move manual form into own component 100%, would using a field Array be a better option? - Coverage will be easier to improve when we split this
const { COLORS } = THEME_CONSTANTS;

const ButtonContainer = styled(Flex)`
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: auto;
`;

const FormSection = styled(Box)`
    ${({ theme }: any) => css`
        margin-bottom: ${theme.space.sm};
    `}
`;

const Select = styled(PlasticSelect)`
    width: 100%;
    box-sizing: border-box;
    background-color: ${COLORS.global.white};
`;
Select.displayName = 'Select';

const SecondaryHeader = ({ label, id, labelVariant, displayTick, ...props }: any) => (
    <Label variant={labelVariant} data-qa-id={`${id}-label`} htmlFor={id} color={COLORS.global.grey500} {...props}>
        <CheckedLabel isSmall={true} isVisible={displayTick}>
            <InlineFlex alignItems="center" data-qm-allow fontSize={['textSmallMobile', 'textSmallDesktop']}>
                {label}
            </InlineFlex>
        </CheckedLabel>
    </Label>
);

SecondaryHeader.propTypes = {
    label: PropTypes.node,
    id: PropTypes.string,
    labelVariant: PropTypes.string,
    displayTick: PropTypes.bool
};

const addressLookupResponseIsValid = (responseObj: any) =>
    !responseObj?.error && responseObj?.data?.matchedAddresses?.length > 0;

// Utility function to normalize and compare addresses
const normalizeAddress = (address: any) => {
    if (!address) return null;

    return {
        houseNumber: (address.houseNumber || '').toLowerCase().trim(),
        houseName: (address.houseName || address.flatName || address.organisationName || '').toLowerCase().trim(),
        flatNumber: (address.flatNumber || '').toLowerCase().trim(),
        streetName: (address.streetName || '').toLowerCase().trim(),
        town: (address.postTown || address.town || '').toLowerCase().trim(),
        postcode: (address.postcode || '').replace(/\s/g, '').toLowerCase()
    };
};

const compareAddresses = (address1: any, address2: any) => {
    const norm1 = normalizeAddress(address1);
    const norm2 = normalizeAddress(address2);
    if (!norm1 || !norm2) return false;

    // Compare key address components
    return (
        norm1.postcode === norm2.postcode &&
        norm1.town === norm2.town &&
        norm1.streetName === norm2.streetName &&
        norm1.houseNumber === norm2.houseNumber &&
        norm1.houseName === norm2.houseName &&
        norm1.flatNumber === norm2.flatNumber
    );
};

export const handleErrorClick = async (event: any, id: any) => {
    event.preventDefault();
    const element = document.getElementById(id);
    if (element) {
        element.focus();
    }
};

const AddressInput = ({ Text, isCurrent, selectedCurrentAddress, setSelectedCurrentAddress, setShowPrevious }: any) => {
    // @ts-expect-error TS2339: Property 'submitAttempted' does not exist on type ''.
    const { submitAttempted } = useLandmarkRefsContext();
    // @ts-expect-error TS2339: Property 'variantTernary' does not exist on type 'null'.
    const { variantTernary } = useContext(FormUtilsContext);
    // @ts-expect-error TS2554: Expected 1 arguments, but got 0.
    const fieldContainerRef = useRef();
    const [selectedAddress, setSelectedAddress] = useState(null);
    // @ts-expect-error TS2339: Property 'showManualForm' does not exist on type 'null'.
    const { showManualForm, setShowManualForm } = useContext(FormUtilsContext);
    const currentOrPrevious = isCurrent ? 'current' : 'previous';
    const isManualForm = showManualForm[currentOrPrevious];
    const [triggerSearch, setTriggerSearch] = useState(false);
    const [hasAttemptedLookup, setHasAttemptedLookup] = useState(false);
    const {
        register,
        formState: { errors, touchedFields },
        watch,
        getFieldState,
        getValues,
        trigger,
        setValue
    } = useFormContext();

    const focusHouseNumberField = () => {
        setTimeout(() => {
            const inputElement = document.getElementById(addressIds.HouseNumber);
            if (inputElement) {
                inputElement.focus();
            }
        }, 0);
    };
    const addressIds = isCurrent ? FORM_SECTIONS.Address.Current : FORM_SECTIONS.Address.Previous;
    const buttonHandler = () => {
        setShowManualForm((prev: any) => ({
            ...prev,
            [currentOrPrevious]: !prev[currentOrPrevious]
        }));
        if (setShowPrevious) setShowPrevious(false);
        focusHouseNumberField();
    };

    const onFocus = useContext(FormFocusContext);
    const handleFocus = (e: any) => {
        if (setShowPrevious) setShowPrevious(true);
        pushToDataLayer({
            event: EVENTS.FIELD_FOCUS,
            fieldName: e.target.name
        });
        // @ts-expect-error TS2349: This expression is not callable.
        if (onFocus) onFocus();
    };

    // TODO: These are currently required for retriggering validation - can this be improved?
    const postcodeValue = watch(addressIds.LookupPostcode);
    watch(addressIds.HouseFlatNumber);
    const flatNumberValue = watch(addressIds.FlatNumber);
    const streetNameValue = watch(addressIds.StreetName);
    const addressLine2Value = watch(addressIds.AddressLine2);
    const townValue = watch(addressIds.Town);
    const countyValue = watch(addressIds.County);
    const houseNumberValue = watch(addressIds.HouseNumber);
    const houseNameValue = watch(addressIds.HouseName);
    const actualPostcodeValue = watch(addressIds.ActualPostcode);

    const { isEdit, xUpstreamEnv } = useFormData();

    useEffect(() => {
        if (isEdit) {
            setShowManualForm((prev: any) => ({
                ...prev,
                [currentOrPrevious]: true
            }));
        }
    }, [isEdit, setShowManualForm, currentOrPrevious]);

    useEffect(() => {
        if (!isCurrent && isManualForm) {
            const fields = [
                addressIds.HouseNumber,
                addressIds.HouseName,
                addressIds.FlatNumber,
                addressIds.StreetName,
                addressIds.AddressLine2,
                addressIds.Town,
                addressIds.County,
                addressIds.ActualPostcode,
                addressIds.YearsAtAddress
            ];

            fields.forEach((field) => {
                const value = getValues(field);
                if (value) {
                    setValue(field, value, { shouldTouch: true });
                }
            });
        }
    }, [isCurrent, isManualForm, addressIds, getValues, setValue]);

    const sessionId = '';
    const [findAddress, submitting, responseObj] = useAddressLookupData(sessionId, xUpstreamEnv);
    const options = [];

    for (let i = 0; i < 32; i++) {
        options.push({
            label: i === 31 ? '31+ years' : `${i} year${i !== 1 ? 's' : ''}`,
            value: i.toString()
        });
    }

    useEffect(() => {
        // TODO: Can this be improved to run less?
        if (triggerSearch && selectedAddress === null) {
            const postcode = getValues(addressIds.LookupPostcode);
            const houseNumber = getValues(addressIds.HouseFlatNumber);
            if (!postcode) return;

            findAddress({
                postcode: postcode.replace(' ', ''),
                premises: houseNumber
            });
            setTriggerSearch(false);
        }
    }, [triggerSearch, getValues, addressIds, findAddress, selectedAddress, trigger]);

    const validateHouseFields = () => {
        const houseNumber = getValues(addressIds.HouseNumber);
        const houseName = getValues(addressIds.HouseName);
        // At least one of the fields must have a value
        const hasHouseNumber = houseNumber && houseNumber.trim().length > 0;
        const hasHouseName = houseName && houseName.trim().length > 0;

        return hasHouseNumber || hasHouseName;
    };

    // Helper function to get current address from form values for comparison
    const getCurrentAddressFromForm = () => {
        // If current address was selected from lookup, use that
        if (selectedCurrentAddress?.formattedAddress) {
            return selectedCurrentAddress;
        }

        // Otherwise, build from current address form fields
        const currentIds = FORM_SECTIONS.Address.Current;
        return {
            houseNumber: getValues(currentIds.HouseNumber),
            houseName: getValues(currentIds.HouseName),
            flatNumber: getValues(currentIds.FlatNumber),
            streetName: getValues(currentIds.StreetName),
            town: getValues(currentIds.Town),
            postcode: getValues(currentIds.ActualPostcode)
        };
    };

    // Validation function for manual address fields
    const validateAddressNotSame = () => {
        if (isCurrent) return true; // Only validate for previous address

        const currentAddress = getCurrentAddressFromForm();
        if (!currentAddress) return true;

        const previousAddress = {
            houseNumber: getValues(addressIds.HouseNumber),
            houseName: getValues(addressIds.HouseName),
            flatNumber: getValues(addressIds.FlatNumber),
            streetName: getValues(addressIds.StreetName),
            town: getValues(addressIds.Town),
            postcode: getValues(addressIds.ActualPostcode)
        };
        if (compareAddresses(currentAddress, previousAddress)) {
            return 'Previous address must be different';
        }

        return true;
    };

    // Trigger validation on both fields when either changes
    useEffect(() => {
        if (houseNumberValue !== undefined || houseNameValue !== undefined) {
            trigger([addressIds.HouseNumber, addressIds.HouseName]);
        }
    }, [houseNumberValue, houseNameValue, trigger, addressIds.HouseNumber, addressIds.HouseName]);

    useEffect(() => {
        trigger(addressIds.AddressLookup);
    }, [selectedAddress, responseObj?.data?.matchedAddresses, responseObj?.error, isManualForm, trigger, addressIds]);
    // Trigger validation on previous address fields when current address changes
    useEffect(() => {
        if (!isCurrent && (selectedCurrentAddress || isManualForm)) {
            // Trigger validation on all previous address fields that have address comparison validation
            trigger([
                addressIds.AddressLookup,
                addressIds.HouseNumber,
                addressIds.FlatNumber,
                addressIds.StreetName,
                addressIds.AddressLine2,
                addressIds.HouseName,
                addressIds.Town,
                addressIds.ActualPostcode,
                addressIds.County
            ]);
        }
    }, [
        selectedCurrentAddress,
        isCurrent,
        trigger,
        addressIds,
        houseNameValue,
        houseNumberValue,
        townValue,
        actualPostcodeValue,
        flatNumberValue,
        streetNameValue,
        addressLine2Value,
        countyValue
    ]);

    return (
        <>
            <Box>
                {/* @ts-expect-error TS2769: No overload matches this call. */}
                <Group ref={fieldContainerRef} data-qa-id={`${currentOrPrevious}-address-group-address`} as="fieldset">
                    <Legend data-qa-id={`${currentOrPrevious}-address-legend-title`} paddingTop="xs">
                        <DefaultText>
                            <Text path={isCurrent ? 'Title' : 'PreviousAddress.Label'}></Text>
                        </DefaultText>
                    </Legend>
                    {!isManualForm && (
                        <>
                            <Group>
                                <FormSection>
                                    <SecondaryHeader
                                        id={addressIds.LookupPostcode}
                                        label={<Text path="Postcode"></Text>}
                                        displayTick={shouldDisplayFormTick(
                                            addressIds.LookupPostcode,
                                            getValues,
                                            errors
                                        )}
                                    />

                                    <FullInputWidth
                                        id={addressIds.LookupPostcode}
                                        {...register(addressIds.LookupPostcode, {
                                            pattern: {
                                                value: postcodeRegex,
                                                message: 'This is not a valid postcode'
                                            },
                                            required: 'Postcode is required'
                                        })}
                                        data-qa-id={addressIds.LookupPostcode}
                                        variant={variantTernary(addressIds.LookupPostcode)}
                                        aria-describedby={getAriaDescribedBy(
                                            addressIds.LookupPostcode,
                                            submitAttempted || hasAttemptedLookup,
                                            [],
                                            errors
                                        )}
                                        autoComplete={autoCompleteMapping['postcode']}
                                    />
                                    <ErrorHandler
                                        id={addressIds.LookupPostcode}
                                        show={touchedFields[addressIds.LookupPostcode]}
                                        data-qm-allow
                                    />
                                </FormSection>
                            </Group>

                            <Group>
                                <FormSection>
                                    <SecondaryHeader
                                        id={addressIds.HouseFlatNumber}
                                        label={<Text path="HouseFlatNumber"></Text>}
                                        displayTick={shouldDisplayFormTick(
                                            addressIds.HouseFlatNumber,
                                            getValues,
                                            errors
                                        )}
                                    />

                                    <FullInputWidth
                                        id={addressIds.HouseFlatNumber}
                                        {...register(addressIds.HouseFlatNumber, {
                                            maxLength: {
                                                value: 32,
                                                message:
                                                    'Your House/flat name or number must be less than 32 characters'
                                            }
                                        })}
                                        data-qa-id={addressIds.HouseFlatNumber}
                                        variant={variantTernary(addressIds.HouseFlatNumber)}
                                        aria-describedby={getAriaDescribedBy(
                                            addressIds.HouseFlatNumber,
                                            submitAttempted || hasAttemptedLookup,
                                            [],
                                            errors
                                        )}
                                    />
                                    <ErrorHandler
                                        id={addressIds.HouseFlatNumber}
                                        show={touchedFields[addressIds.HouseFlatNumber]}
                                        data-qm-allow
                                    />
                                </FormSection>
                            </Group>
                        </>
                    )}
                    {isManualForm && (
                        <>
                            {/* @ts-expect-error TS2322: Type '"500"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
                            <PlasticText size={'small'} fontWeight={'500'} color={COLORS.global.grey600}>
                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.HouseNumber}
                                            label={<Text path="HouseNumber"></Text>}
                                            displayTick={shouldDisplayFormTick(
                                                addressIds.HouseNumber,
                                                getValues,
                                                errors
                                            )}
                                        />

                                        <FullInputWidth
                                            id={addressIds.HouseNumber}
                                            {...register(addressIds.HouseNumber, {
                                                pattern: {
                                                    value: houseFlatNumberRegex,
                                                    message: 'This is not a valid house number'
                                                },
                                                maxLength: {
                                                    value: 32,
                                                    message: 'Your house number must be 32 characters or less'
                                                },
                                                validate: (value: any) => {
                                                    // @ts-expect-error TS2554: Expected 0 arguments, but got 1.
                                                    const houseFieldsValid = validateHouseFields(value);
                                                    if (!houseFieldsValid) {
                                                        return 'Please enter a house number';
                                                    }
                                                    const addressValidation = validateAddressNotSame();
                                                    return addressValidation;
                                                }
                                            })}
                                            data-qa-id={addressIds.HouseNumber}
                                            variant={variantTernary(addressIds.HouseNumber)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.HouseNumber,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['house-number']}
                                        />
                                        <ErrorHandler
                                            id={addressIds.HouseNumber}
                                            show={touchedFields[addressIds.HouseNumber]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.HouseName}
                                            label={<Text path="HouseName"></Text>}
                                            displayTick={shouldDisplayFormTick(addressIds.HouseName, getValues, errors)}
                                        />

                                        <FullInputWidth
                                            id={addressIds.HouseName}
                                            {...register(addressIds.HouseName, {
                                                pattern: {
                                                    value: houseFlatNumberRegex,
                                                    message: 'This is not a valid house/building name'
                                                },
                                                maxLength: {
                                                    value: 32,
                                                    message: 'Your house/building name must be 32 characters or less'
                                                },
                                                validate: (value: any) => {
                                                    // @ts-expect-error TS2554: Expected 0 arguments, but got 1.
                                                    const houseFieldsValid = validateHouseFields(value);
                                                    if (!houseFieldsValid) {
                                                        return 'Please enter a house/building name';
                                                    }
                                                    const addressValidation = validateAddressNotSame();
                                                    return addressValidation;
                                                }
                                            })}
                                            data-qa-id={addressIds.HouseName}
                                            variant={variantTernary(addressIds.HouseName)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.HouseName,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['house-name']}
                                        />
                                        <ErrorHandler
                                            id={addressIds.HouseName}
                                            show={touchedFields[addressIds.HouseName]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.FlatNumber}
                                            label={<Text path="FlatNumber"></Text>}
                                            displayTick={shouldDisplayFormTick(
                                                addressIds.FlatNumber,
                                                getValues,
                                                errors
                                            )}
                                        />

                                        <FullInputWidth
                                            id={addressIds.FlatNumber}
                                            {...register(addressIds.FlatNumber, {
                                                pattern: {
                                                    value: houseFlatNumberRegex,
                                                    message: 'This is not a valid flat number'
                                                },
                                                maxLength: {
                                                    value: 32,
                                                    message: 'Your flat number must be 32 characters or less'
                                                },
                                                validate: () => validateAddressNotSame()
                                            })}
                                            data-qa-id={addressIds.FlatNumber}
                                            variant={variantTernary(addressIds.FlatNumber)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.FlatNumber,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['flat-number']}
                                        />
                                        <ErrorHandler
                                            id={addressIds.FlatNumber}
                                            show={touchedFields[addressIds.FlatNumber]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.StreetName}
                                            label={<Text path="StreetName"></Text>}
                                            displayTick={shouldDisplayFormTick(
                                                addressIds.StreetName,
                                                getValues,
                                                errors
                                            )}
                                        />

                                        <FullInputWidth
                                            id={addressIds.StreetName}
                                            {...register(addressIds.StreetName, {
                                                pattern: {
                                                    value: /^[0-9a-zA-Z'`,. -]+$/,
                                                    message: 'This is not a valid street name'
                                                },
                                                maxLength: {
                                                    value: 40,
                                                    message: 'Your street name must be 40 characters or less'
                                                },
                                                validate: () => validateAddressNotSame()
                                            })}
                                            data-qa-id={addressIds.StreetName}
                                            variant={variantTernary(addressIds.StreetName)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.StreetName,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['street-name']}
                                        />
                                        <ErrorHandler
                                            id={addressIds.StreetName}
                                            show={touchedFields[addressIds.StreetName]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.AddressLine2}
                                            label={<Text path="AddressLine2"></Text>}
                                            displayTick={shouldDisplayFormTick(
                                                addressIds.AddressLine2,
                                                getValues,
                                                errors
                                            )}
                                        />

                                        <FullInputWidth
                                            id={addressIds.AddressLine2}
                                            {...register(addressIds.AddressLine2, {
                                                pattern: {
                                                    value: /^[0-9a-zA-Z'`,. -]+$/,
                                                    message: 'This is not a valid address line 2'
                                                },
                                                maxLength: {
                                                    value: 32,
                                                    message: 'Your address line 2 must be 32 characters or less'
                                                },
                                                validate: () => validateAddressNotSame()
                                            })}
                                            data-qa-id={addressIds.AddressLine2}
                                            variant={variantTernary(addressIds.AddressLine2)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.AddressLine2,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['address-line-2']}
                                        />

                                        <ErrorHandler
                                            id={addressIds.AddressLine2}
                                            show={touchedFields[addressIds.AddressLine2]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.Town}
                                            label={<Text path="Town"></Text>}
                                            displayTick={shouldDisplayFormTick(addressIds.Town, getValues, errors)}
                                        />

                                        <FullInputWidth
                                            id={addressIds.Town}
                                            {...register(addressIds.Town, {
                                                required: 'Town/city is required',
                                                maxLength: {
                                                    value: 32,
                                                    message: 'Your town/city must be 32 characters or less'
                                                },
                                                pattern: {
                                                    value: /^[0-9a-zA-Z'`,. -]+$/,
                                                    message: 'This is not a valid town/city'
                                                },
                                                validate: () => validateAddressNotSame()
                                            })}
                                            data-qa-id={addressIds.Town}
                                            variant={variantTernary(addressIds.Town)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.Town,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['town-city']}
                                        />

                                        <ErrorHandler
                                            id={addressIds.Town}
                                            show={touchedFields[addressIds.Town]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.County}
                                            label={<Text path="County"></Text>}
                                            displayTick={shouldDisplayFormTick(addressIds.County, getValues, errors)}
                                        />

                                        <FullInputWidth
                                            id={addressIds.County}
                                            {...register(addressIds.County, {
                                                pattern: {
                                                    value: /^[0-9a-zA-Z'`,. -]+$/,
                                                    message: 'This is not a valid county'
                                                },
                                                maxLength: {
                                                    value: 32,
                                                    message: 'Your county must be 32 characters or less'
                                                },
                                                validate: () => validateAddressNotSame()
                                            })}
                                            data-qa-id={addressIds.County}
                                            variant={variantTernary(addressIds.County)}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.County,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['county']}
                                        />
                                        <ErrorHandler
                                            id={addressIds.County}
                                            show={touchedFields[addressIds.County]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <Group>
                                    <FormSection>
                                        <SecondaryHeader
                                            id={addressIds.ActualPostcode}
                                            label={<Text path="Postcode"></Text>}
                                            displayTick={shouldDisplayFormTick(
                                                addressIds.ActualPostcode,
                                                getValues,
                                                errors
                                            )}
                                        />

                                        <FullInputWidth
                                            id={addressIds.ActualPostcode}
                                            {...register(addressIds.ActualPostcode, {
                                                pattern: {
                                                    value: postcodeRegex,
                                                    message: 'This is not a valid postcode'
                                                },
                                                required: 'Postcode is required',
                                                validate: () => validateAddressNotSame()
                                            })}
                                            data-qa-id={addressIds.ActualPostcode}
                                            aria-describedby={getAriaDescribedBy(
                                                addressIds.ActualPostcode,
                                                submitAttempted,
                                                [],
                                                errors
                                            )}
                                            autoComplete={autoCompleteMapping['postcode']}
                                            variant={variantTernary(addressIds.ActualPostcode)}
                                        />
                                        <ErrorHandler
                                            id={addressIds.ActualPostcode}
                                            show={touchedFields[addressIds.ActualPostcode]}
                                            data-qm-allow
                                        />
                                    </FormSection>
                                </Group>

                                <FormSection>
                                    <ButtonContainer>
                                        <Button
                                            data-qa-id={addressIds.SearchAddressLink}
                                            variant="textLink"
                                            onClick={buttonHandler}
                                            my={'xs'}
                                            // @ts-expect-error TS2322: Type '"0"' is not assignable to type 'ResponsiveValue<AllowedSpace, DefaultTheme> | undefined'.
                                            py="0"
                                            style={{ fontWeight: '600' }}
                                        >
                                            <Text path="SearchAddressLink"></Text>
                                        </Button>
                                    </ButtonContainer>
                                </FormSection>
                            </PlasticText>
                        </>
                    )}
                </Group>

                {!isManualForm && (
                    <>
                        <ButtonContainer>
                            <Button
                                id={`find-${currentOrPrevious}-address-button`}
                                data-qa-id={`find-${currentOrPrevious}-address-button`}
                                variant="secondary"
                                onClick={() => {
                                    setHasAttemptedLookup(true);
                                    if (getFieldState(addressIds.LookupPostcode).invalid)
                                        return handleErrorClick(event, addressIds.LookupPostcode);
                                    if (getFieldState(addressIds.HouseFlatNumber).invalid)
                                        return handleErrorClick(event, addressIds.HouseFlatNumber);
                                    setSelectedAddress(null);
                                    setTriggerSearch(true);
                                }}
                                aria-describedby={getAriaDescribedBy(
                                    addressIds.AddressLookup,
                                    submitAttempted,
                                    [],
                                    errors
                                )}
                            >
                                <Text path="FindAddressButton"></Text>
                            </Button>

                            <Button
                                data-qa-id={addressIds.EnterManuallyLink}
                                variant="textLink"
                                onClick={buttonHandler}
                                my={['2.4rem', 'md']}
                                // @ts-expect-error TS2322: Type '"0"' is not assignable to type 'ResponsiveValue<AllowedSpace, DefaultTheme> | undefined'.
                                py="0"
                                style={{ fontWeight: '600' }}
                            >
                                <Text path="LookupAddressLink"></Text>
                            </Button>
                        </ButtonContainer>
                    </>
                )}
            </Box>
            <AddressLookup
                // @ts-expect-error TS2769: No overload matches this call.
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                currentOrPrevious={currentOrPrevious}
                findAddress={findAddress}
                submitting={submitting}
                responseObj={responseObj}
                setSelectedCurrentAddress={setSelectedCurrentAddress}
                {...register(addressIds.AddressLookup, {
                    validate: () => {
                        if (isManualForm) {
                            return true;
                        }
                        // If there's an actual error from the API or lookup returns no results if the user is manually entering their address
                        if (responseObj?.error || responseObj?.data?.matchedAddresses?.length === 0) {
                            return 'Address lookup failed, please enter address manually';
                        }

                        // For previous address, check if it matches current address
                        if (!isCurrent && selectedCurrentAddress && selectedAddress) {
                            if (compareAddresses(selectedAddress, selectedCurrentAddress)) {
                                return 'Previous address must be different';
                            }
                        }

                        // For all cases (including when valid addresses are available), check if address is provided
                        const isValid = !!selectedAddress;
                        return isValid || 'Please provide your address';
                    }
                })}
            ></AddressLookup>

            <ErrorHandler
                id={addressIds.AddressLookup}
                name={'AddressLookup'}
                textStore={'Address'}
                ariaRole={'alert'}
                show={
                    (submitAttempted && !isManualForm && !submitting) ||
                    (!addressLookupResponseIsValid(responseObj) &&
                        !isManualForm &&
                        hasAttemptedLookup &&
                        !submitting) ||
                    JSON.stringify(selectedAddress) === JSON.stringify(selectedCurrentAddress)
                }
                onClick={(event: any) => {
                    if (responseObj?.error || responseObj?.data?.matchedAddresses?.length === 0) {
                        setShowManualForm((prev: any) => ({
                            ...prev,
                            [currentOrPrevious]: true
                        }));
                        // Clear the error by triggering validation now that manual form is shown
                        setTimeout(() => trigger(addressIds.AddressLookup), 0);
                        focusHouseNumberField();
                        return;
                    }
                    if (validateAddressNotSame() !== true) {
                        // @ts-expect-error TS2339: Property 'data' does not exist on type 'boolean | ((reqData: any) => void) |  error: string; respons
                        if (selectedAddress && responseObj?.data?.matchedAddresses?.length > 1) {
                            setSelectedAddress(null);
                            return setTimeout(
                                () => handleErrorClick(event, 'address:previous-address:list-radio-button-0-input'),
                                0
                            );
                        } else
                            return handleErrorClick(
                                event,
                                isManualForm ? addressIds.HouseNumber : addressIds.LookupPostcode
                            );
                    }
                    if (!postcodeValue) return handleErrorClick(event, addressIds.LookupPostcode);
                    // @ts-expect-error TS2339: Property 'data' does not exist on type 'boolean | ((reqData: any) => void) |  error: string; respons
                    else if (!selectedAddress && responseObj?.data?.matchedAddresses?.length > 0)
                        return handleErrorClick(
                            event,
                            `address:${currentOrPrevious}-address:list-radio-button-0-input`
                        );
                    else handleErrorClick(event, `find-${currentOrPrevious}-address-button`);
                }}
            ></ErrorHandler>

            {(selectedAddress || isManualForm) && (
                <>
                    <Group>
                        <FormSection>
                            <LabelSection
                                id={addressIds.YearsAtAddress}
                                label={
                                    <Text
                                        path={
                                            isCurrent
                                                ? 'CurrentAddress.TimeAtAddressLabel'
                                                : 'PreviousAddress.TimeAtAddressLabel'
                                        }
                                    ></Text>
                                }
                                displayTick={shouldDisplayFormTick(addressIds.YearsAtAddress, getValues, errors)}
                                data-qm-allow
                            />
                            <Select
                                id={addressIds.YearsAtAddress}
                                data-qa-id={addressIds.YearsAtAddress}
                                onFocus={handleFocus}
                                // @ts-expect-error TS2783: 'name' is specified more than once, so this usage will be overwritten.
                                name={addressIds.YearsAtAddress}
                                variant={variantTernary(addressIds.YearsAtAddress)}
                                aria-describedby={getAriaDescribedBy(
                                    addressIds.YearsAtAddress,
                                    submitAttempted,
                                    [],
                                    errors
                                )}
                                autoComplete={'off'}
                                defaultValue=""
                                {...register(addressIds.YearsAtAddress, {
                                    required: 'Please select an option for years at your address'
                                })}
                            >
                                <option value="" disabled>
                                    Please select
                                </option>
                                {options.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </Select>

                            <ErrorHandler
                                id={addressIds.YearsAtAddress}
                                name={addressIds.YearsAtAddress}
                                textStore={'Address'}
                                show={true}
                            />
                        </FormSection>
                    </Group>
                </>
            )}
        </>
    );
};

AddressInput.propTypes = {
    Text: PropTypes.func.isRequired,
    isCurrent: PropTypes.bool,
    setSelectedCurrentAddress: PropTypes.func,
    selectedCurrentAddress: PropTypes.object,
    setShowPrevious: PropTypes.func
};

export default withText('Address')(AddressInput);
